<template>
  <div class="transparent headers-initial">
    <f7-block-title class="block-title-result">
      <span :class="`color-title-${selection}`">{{ `${$t('result.title_weekly_irrigation')}` }}</span>
    </f7-block-title>
    <div
      style="padding-top: 2rem"
      class="content-page-result"
    >
      <DxDataGrid
        :data-source="data"
        :allow-column-reordering="false"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
        :column-auto-width="true"
        :row-alternation-enabled="false"
        :word-wrap-enabled="true"
        no-data-text="Sin datos"
      >
        <DxSelection
          mode="single"
        />
        <DxExport
          :enabled="true"
          :texts="{exportAll: 'Exportar',}"
        />
        <DxPaging
          :page-size="pageSize"
          :page-index="pageIndex"
        />
        <DxPager
          :show-page-size-selector="true"
          :show-info="true"
          info-text="Página {0} de {1} ({2} días)"
          :allowed-page-sizes="['7', '14', '21', '28', '56']"
        />
        <DxHeaderFilter :visible="true" />
        <DxSearchPanel
          text=""
          :visible="true"
          :width="200"
          :placeholder="$t('Search_placeholder')"
        />
        <DxColumnFixing :enabled="false" />
        <DxColumn
          data-field="ddt"
          :caption="$t('result.irrigation.ddt')"
          :allow-header-filtering="false"
          alignment="left"
        />
        <DxColumn
          data-field="crop_cycle_week"
          :caption="$t('result.irrigation.crop_cycle_week')"
          :allow-header-filtering="false"
          alignment="left"
        />
        <DxColumn
          data-field="weekday"
          :caption="$t('result.irrigation.weekday')"
          :allow-header-filtering="false"
          alignment="left"
        />
        <DxColumn
          :data-field="TO_FIXED_DECIMALS_IN_TABLES
            ? 'irrigation_volume_fixed' : 'irrigation_volume'"
          :caption="$t('result.irrigation.vol_irr')"
          :allow-header-filtering="false"
          alignment="left"
        />
        <DxColumn
          :data-field="TO_FIXED_DECIMALS_IN_TABLES ? 'irrigation_time_fixed' : 'irrigation_time'"
          :caption="$t('result.irrigation.tim_irr')"
          :allow-header-filtering="false"
          alignment="left"
        />
      </DxDataGrid>
    </div>
    <div
      class="select-button"
      style="padding-top: 1rem"
    >
      <div class="content-note">
        <span class="note">
          El usuario puede reajustar el riego según las condiciones reales.
          Para ello hay dos posibilidades opcionales:
          Dia muy nublado o lluvia (aportar la mitad de la ETc).
          Vientos muy secos (Aportar el doble de la Etc).
        </span>
      </div>
      <DxButton
        name="btnSignIn"
        class="selected-button"
        style="color:#FFFFFF"
        @click="openSensorIrrigation"
      >
        {{ 'Manejo correctivo del riego con sensores' }}
      </DxButton>
    </div>
    <DxPopUpSensorIrrigation />
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxSearchPanel,
  DxExport,
  DxColumnFixing,
  DxHeaderFilter,
  DxPager,
  DxSelection,
} from 'devextreme-vue/data-grid';
import DxButton from 'devextreme-vue/button';
import { mapState, mapActions } from 'vuex';
import DxPopUpSensorIrrigation from './popUpSensorIrrigation/index.vue';
import { NUMBER_OF_DECIMALS, TO_FIXED_DECIMALS_IN_TABLES } from '../../js/constants';

export default {
  name: 'IrrigationGreenhouseResult',
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxSearchPanel,
    DxExport,
    DxColumnFixing,
    DxHeaderFilter,
    DxPager,
    DxSelection,
    DxButton,
    DxPopUpSensorIrrigation,
  },
  props: {
    result: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      data: [],
      NUMBER_OF_DECIMALS,
      TO_FIXED_DECIMALS_IN_TABLES,
      pageSize: 14,
      pageIndex: 0,
    };
  },
  computed: {
    ...mapState('authentication', [
      'selection',
    ]),
  },
  mounted() {
    this.data = this.result.irrigation_time.map((irrigationTime, index) => ({
      ddt: index,
      date: this.result.dates[index],
      irrigation_time: irrigationTime.toFixed(0),
      irrigation_volume: this.result.irrigation_volume[index],
      irrigation_time_fixed: irrigationTime
        .toFixed(0),
      irrigation_volume_fixed: this.result.irrigation_volume[index]
        .toFixed(this.NUMBER_OF_DECIMALS),
      crop_cycle_week: Math.trunc(index / 7) + 1,
      weekday: this.getWeekDay(this.result.dates[index]),
    }));
    if (this.data.length) {
      switch (this.data[0].weekday) {
        case 'M':
          this.data.unshift({
            weekday: 'L',
          });
          break;
        case 'Mierc':
          this.data.unshift(
            { weekday: 'L' }, { weekday: 'M' },
          );
          break;
        case 'J':
          this.data.unshift(
            { weekday: 'L' }, { weekday: 'M' }, { weekday: 'Mierc' },
          );
          break;
        case 'V':
          this.data.unshift(
            { weekday: 'L' }, { weekday: 'M' }, { weekday: 'Mierc' }, { weekday: 'J' },
          );
          break;
        case 'S':
          this.data.unshift(
            { weekday: 'L' }, { weekday: 'M' }, { weekday: 'Mierc' }, { weekday: 'J' }, { weekday: 'V' },
          );
          break;
        case 'D':
          this.data.unshift(
            { weekday: 'L' },
            { weekday: 'M' },
            { weekday: 'Mierc' },
            { weekday: 'J' },
            { weekday: 'V' },
            { weekday: 'S' },
          );
          break;
        case 'L':
        default:
          break;
      }
    }
    const now = new Date(Date.now());
    function padTo2Digits(num) {
      return num.toString().padStart(2, '0');
    }

    function formatDate(date) {
      return [
        padTo2Digits(date.getDate()),
        padTo2Digits(date.getMonth() + 1),
        date.getFullYear(),
      ].join('/');
    }
    const nowString = formatDate(now).toString();
    const indexToday = this.result.dates.findIndex((date) => date === nowString);
    if (indexToday !== -1) {
      this.pageIndex = Math.trunc(indexToday / this.pageSize);
    }
  },
  methods: {
    getWeekDay(strDate) {
      const [day, month, year] = strDate.split('/');
      const date = new Date(+year, month - 1, +day);
      const weekday = date.getDay();
      switch (weekday) {
        case 0:
          return 'D';
        case 1:
          return 'L';
        case 2:
          return 'M';
        case 3:
          return 'Mierc';
        case 4:
          return 'J';
        case 5:
          return 'V';
        case 6:
          return 'S';
        default:
          return weekday;
      }
    },
    onChangeProvince() {

    },
    onChangeManure() {

    },
    goToInfoIrrigation() {
      this.$f7.views.main.router.navigate('/infoIrrigation/', { reloadCurrent: true });
    },
    goToDetailInformation() {
      this.$f7.views.main.router.navigate('/detailInformation/', { reloadCurrent: true });
    },
    openSensorIrrigation() {
      this.setEnabledPopUpSensor(true);
    },
    ...mapActions('Result', [
      'setEnabledPopUpSensor',
    ]),
  },
};
</script>
  <style
    lang="scss"
    scoped
  >
  @import "../../css/mixins";
    .boton-greenHouse {
    background: #FF7F26;
    height: 30px;
    width: 100px;
    margin-top: 60px;
    text-align: center;
    }
    .boton-openAir {
    background: #51A411;
    height: 30px;
    width: 100px;
    margin-top: 60px;
    text-align: center;
    }
    .content-checkbox {
    padding-top: 50px;
    padding-left: 40px;
    }
    .content-devx {
        height: 500px !important;
    }
    .selected-button {
      display: flex;
      background: #FF7F26 !important;
      width: 250px;
      border-radius: 4px !important;
      height: 90px !important;
      box-shadow: none;
      font-size: 20px !important;
      line-height: 20px !important;
    }
  .select-button {
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
    padding-top: 150px;
    align-items: center !important;
    font-size: 25px !important;
  }
    .footer-note {
      display: flex;
      align-items: center;
      justify-content: center;
      bottom: 0;
      right: 0;
      left: 0;
    }
    .content-note {
      margin: 2rem;
      max-width: 1080px;
    }
    .note {
      font-family: 'Roboto';
      font-style: normal;
      font-weight: 600;
      text-align: center;
      line-height: normal;
      color: steelblue;
      font-size: small;
      @include md {
        font-size: large;
        line-height: 28px;
      }
    }
  </style>
